import { type Answer, type ConsentStamp, type Field, RESPONSE_SHAPE, type Section } from '@eformulieren/model'
import { sha256Hex } from './sha256.js'
import { isEmpty, type State } from './state.js'

/**
 * Het akkoord per persoon (besluit van de product owner van 3 november 2026, eisenboek 12.56, ontwerp 6.2).
 *
 * Ieder die de sectie mag bewerken geeft voor zichzelf akkoord met de inhoud, en ziet wie het verder gaf.
 * Er is geen lijst van wie akkoord moet geven. Een akkoord rust op de inhoud van dat moment, en vervalt
 * zodra iemand die inhoud verandert ("zuiver"). Staat `verplicht` op het veld, dan vraagt indienen minstens
 * één akkoord dat nog geldt; zonder die regel legt het veld alleen vast wie akkoord gaf.
 *
 * **Welke inhoud telt.** De antwoorden van elk veld van de instantie, in beide lagen, zonder wie en wanneer
 * (de herkomst) en zonder de akkoorden zelf: elk akkoord per persoon en elk akkoordveld. Een akkoord geven
 * verandert de inhoud niet, en de verklaring van de indiener is een verklaring en geen inhoud. Van rich
 * text telt de tekst en niet de opmaak (`structuur`), want daar staan ook de ankers van opmerkingen in, en
 * een opmerking is geen inhoud. Een bijlage telt met haar hash en naam, een herhaalgroep met haar rijen in
 * volgorde en hun cellen. Een leeg antwoord telt als geen antwoord. Een verwijzing, een criterium en een
 * component hebben geen antwoord in de stand.
 *
 * **Wie rekent.** Alleen de pod rekent de hash uit, over de hele stand die hij heeft (`standHash`), en zet
 * hem als `stand` in het antwoord van het veld; een stempel geldt als zijn `stand` die van het antwoord is.
 * De browser rekent dus niet zelf: een medeschrijver ziet niet elke sectie, en zou een andere hash vinden.
 * Wat browser en pod wel allebei doen, is deze regel over hetzelfde antwoord (ontwerp 5.9); bij het
 * indienen zet de pod de stand eerst vers, zodat de poort over de inhoud van dat moment rekent.
 */

/** Is dit veld een akkoord, van welke soort ook? Die tellen niet als inhoud. */
export function isConsent(field: Pick<Field, 'control'> & { readonly akkoord?: boolean | undefined }): boolean {
  return field.control === 'akkoordPerPersoon' || (field.control === 'toggle' && field.akkoord === true)
}

/** De inhoud van een antwoord, zonder herkomst en zonder opmaak, of niets als het leeg is. */
function contentOf(answer: Answer | undefined): unknown {
  if (answer === undefined || isEmpty(answer)) return undefined
  if ('rijen' in answer) {
    return answer.rijen.map((row) => [
      row.rijId,
      Object.keys(row.waarden)
        .sort()
        .flatMap((cellId) => {
          const cell = contentOf(row.waarden[cellId])
          return cell === undefined ? [] : [[cellId, cell]]
        }),
    ])
  }
  if ('documenten' in answer) return answer.documenten.map((document) => [document.hash, document.bestandsnaam])
  if ('waarden' in answer) return answer.waarden
  return answer.waarde
}

/** De hash van de inhoud van deze stand over deze secties, als `sha256:` met 64 hextekens. */
export function standHash(sections: readonly Section[], state: State): string {
  const entries: [string, unknown][] = []
  for (const section of sections) {
    for (const field of section.velden) {
      if (isConsent(field) || RESPONSE_SHAPE[field.control] === 'geen' || RESPONSE_SHAPE[field.control] === 'laag') continue
      const content = contentOf(state[field.id])
      if (content !== undefined) entries.push([field.id, content])
    }
  }
  entries.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
  return `sha256:${sha256Hex(JSON.stringify(entries))}`
}

export interface ConsentEntry extends ConsentStamp {
  /** Of het akkoord nog over de inhoud van nu gaat; anders is het vervallen na een wijziging. */
  readonly geldig: boolean
}

export interface ConsentOverview {
  /** Elk gegeven akkoord, op volgorde van geven. */
  readonly akkoorden: readonly ConsentEntry[]
  /** Hoeveel er nog gelden. */
  readonly geldig: number
}

/** De akkoorden in dit antwoord en of ze nog gelden; een ander antwoord heeft er geen. */
export function consentOverview(answer: Answer | undefined): ConsentOverview {
  if (answer === undefined || !('waarde' in answer) || answer.akkoorden === undefined) return { akkoorden: [], geldig: 0 }
  const current = answer.stand
  const akkoorden = [...answer.akkoorden]
    .sort((a, b) => (a.gegevenOp < b.gegevenOp ? -1 : a.gegevenOp > b.gegevenOp ? 1 : 0))
    .map((stamp) => ({ ...stamp, geldig: current !== undefined && stamp.stand === current }))
  return { akkoorden, geldig: akkoorden.filter((entry) => entry.geldig).length }
}

/** Is dit het antwoord van een akkoord per persoon? Dan is `verplicht` minstens één geldig akkoord. */
export function isConsentAnswer(answer: Answer | undefined): boolean {
  return answer !== undefined && 'waarde' in answer && answer.akkoorden !== undefined
}
